/**
 * UtilitySection - Utility Tab Content
 *
 * Extracts the utility configuration from ScenarioEditor Tab 1.
 * Contains three sub-tabs:
 * - Electricity: Tariff selection and rate configuration
 * - Fuel: Gas/diesel fuel tariffs
 * - CA DER Charges: California DER charges configuration
 */

import React, { useState } from 'react';
import {
    Box,
    Tab,
    Tabs,
    Alert,
    FormControlLabel,
    Checkbox,
    RadioGroup,
    Radio,
    FormControl,
    FormLabel,
} from '@mui/material';
import { ElectricBolt, Settings, AttachMoney } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { SectionCard, vortxColors, debug, convertGasTariffForSolver, CustomRateBuilder, vortxTypography } from '@vortx/shared-ui';
import UnifiedTariffSelector from '../../../components/scenario/UnifiedTariffSelector';
import ExportCompensationConfig from '../../../components/scenario/ExportCompensationConfig';
import FuelTariffConfiguration from '../../../components/scenario/FuelTariffConfiguration';
import { CADERChargesPanel } from '../../../components/scenario';

/**
 * Map utility names to utility codes for CA DER
 */
const mapUtilityNameToCode = (utilityName) => {
    if (!utilityName) return null;
    const name = utilityName.toLowerCase();
    if (name.includes('pacific gas') || name.includes('pg&e') || name.includes('pge')) {
        return 'PGE';
    }
    if (name.includes('southern california edison') || name.includes('sce') || name.includes('edison')) {
        return 'SCE';
    }
    if (name.includes('san diego') || name.includes('sdge') || name.includes('sdg&e')) {
        return 'SDGE';
    }
    return null;
};

/**
 * Electricity Sub-tab
 * Handles tariff selection and rate configuration.
 */
function ElectricitySubTab({
    scenarioForm,
    vortxSettings,
    setVortxSettings,
    optimizationEngine,
    projectId,
    showValidation,
    requiredTariff,
}) {
    // State to track tariff input method: 'database' or 'manual'
    // Initialize based on whether useManualRate is true or if there's custom tariff data
    const [tariffInputMode, setTariffInputMode] = useState(() => {
        if (scenarioForm.useManualRate || scenarioForm.blendedEnergyRate) {
            return 'manual';
        }
        if (scenarioForm.customTariffData) {
            return 'database';
        }
        return 'database'; // Default to database
    });

    return (
        <SectionCard title={`Tariff Selection ${showValidation && !requiredTariff ? '*' : ''}`} icon={<Settings color="primary" />}>
            {showValidation && !requiredTariff && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Tariff selection is required.
                </Alert>
            )}
            <FormControlLabel
                control={
                    <Checkbox
                        checked={scenarioForm.offGrid}
                        onChange={(e) => scenarioForm.setOffGrid(e.target.checked)}
                    />
                }
                label="Off-Grid System"
            />
            {!scenarioForm.offGrid && (
                <Box>
                    {/* Tariff Input Method Selection */}
                    <FormControl component="fieldset" sx={{ mb: 3, mt: 2 }}>
                        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Tariff Input Method
                        </FormLabel>
                        <RadioGroup
                            row
                            value={tariffInputMode}
                            onChange={(e) => {
                                const newMode = e.target.value;
                                setTariffInputMode(newMode);
                                // Set useManualRate flag based on mode
                                if (newMode === 'manual') {
                                    scenarioForm.setUseManualRate(true);
                                } else {
                                    scenarioForm.setUseManualRate(false);
                                }
                            }}
                        >
                            <FormControlLabel
                                value="database"
                                control={<Radio />}
                                label="Select from Database"
                            />
                            <FormControlLabel
                                value="manual"
                                control={<Radio />}
                                label="Enter Manually"
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* Unified Tariff Selector (Vortx Flux Rate Database) - Only show when database mode selected */}
                    {tariffInputMode === 'database' && (
                        <UnifiedTariffSelector
                        selectedTariff={scenarioForm.customTariffData || scenarioForm.selectedTariffData}
                        onTariffSelect={(tariff) => {
                            debug.log('Selected Tariff from UnifiedTariffSelector:', tariff);

                            // Unwrap tariff data if it's from Flux Rate Database schema
                            let tariffDataToUse = tariff;

                            if (tariff.rate_data && tariff.rate_data.rate_data) {
                                tariffDataToUse = tariff.rate_data.rate_data;
                            }

                            // Ensure ID, Name, voltage_level, and source persisted
                            tariffDataToUse = {
                                ...tariffDataToUse,
                                id: tariff.id,
                                name: tariff.name || tariff.label,
                                match_source: tariff.match_source,
                                voltage_level: tariff.voltage_level || tariff.rate_data?.voltage_level,
                                utility_name: tariff.utility_name || tariffDataToUse.utility_name,
                                rate_schedule_name: tariff.rate_schedule_name || tariffDataToUse.rate_schedule_name,
                            };

                            scenarioForm.setCustomTariffData(tariffDataToUse);
                            // Don't set useManualRate - database selection should keep it false
                        }}
                        matchSource={scenarioForm.customTariffData?.match_source || scenarioForm.selectedTariffData?.match_source}
                        />
                    )}

                    {/* Rate Builder - Only show when manual mode selected OR when viewing a loaded tariff */}
                    {(tariffInputMode === 'manual' || scenarioForm.customTariffData) && (
                        <CustomRateBuilder
                        blendedEnergyRate={scenarioForm.blendedEnergyRate}
                        setBlendedEnergyRate={scenarioForm.setBlendedEnergyRate}
                        blendedDemandRate={scenarioForm.blendedDemandRate}
                        setBlendedDemandRate={scenarioForm.setBlendedDemandRate}
                        monthlyFixedCharge={scenarioForm.monthlyFixedCharge}
                        setMonthlyFixedCharge={scenarioForm.setMonthlyFixedCharge}
                        vortxSettings={vortxSettings}
                        setVortxSettings={setVortxSettings}
                        optimizationEngine={optimizationEngine}
                        projectId={projectId}
                        customTariffData={scenarioForm.customTariffData}
                        setCustomTariffData={scenarioForm.setCustomTariffData}
                        renderExportCompensation={scenarioForm.usePv ? () => (
                            <ExportCompensationConfig
                                utilityCode={mapUtilityNameToCode(scenarioForm.customTariffData?.utility_name || scenarioForm.utilityName)}
                                value={scenarioForm.exportConfig}
                                onChange={scenarioForm.setExportConfig}
                            />
                        ) : undefined}
                        />
                    )}
                </Box>
            )}
        </SectionCard>
    );
}

/**
 * Fuel Sub-tab
 * Handles diesel and natural gas fuel tariff configuration.
 */
function FuelSubTab({ scenarioForm, vortxStyles, projectId }) {
    return (
        <SectionCard title="Fuel Tariffs" icon={<Settings color="primary" />}>
            <FuelTariffConfiguration
                dieselFuelCostPerGallon={scenarioForm.generatorFuelCostPerGallon}
                setDieselFuelCostPerGallon={scenarioForm.setGeneratorFuelCostPerGallon}
                naturalGasCostPerMmbtu={scenarioForm.generatorFuelCostPerMmbtu}
                setNaturalGasCostPerMmbtu={scenarioForm.setGeneratorFuelCostPerMmbtu}
                vortxStyles={vortxStyles}
                projectId={projectId}
                initialGasTariffId={scenarioForm.gasTariffId}
                initialGasTariffData={scenarioForm.gasTariffData}
                onGasTariffChange={(tariff) => {
                    if (tariff) {
                        scenarioForm.setGasTariffId(tariff.tariffId);
                        scenarioForm.setGasTariffData(tariff.tariffData);

                        // Update the fuel cost field with the effective rate from the tariff
                        try {
                            const converted = convertGasTariffForSolver(tariff.tariffData);
                            if (converted?.tiers?.length > 0) {
                                const ratePerMmbtu = converted.tiers[0]?.rate_per_mmbtu;
                                if (ratePerMmbtu && ratePerMmbtu > 0) {
                                    debug.log(`Updating fuel cost from gas tariff: $${ratePerMmbtu.toFixed(2)}/MMBtu`);
                                    scenarioForm.setGeneratorFuelCostPerMmbtu(ratePerMmbtu.toFixed(2));
                                }
                            }
                        } catch (err) {
                            debug.warn('Failed to convert gas tariff:', err);
                        }
                    } else {
                        scenarioForm.setGasTariffId(null);
                        scenarioForm.setGasTariffData(null);
                    }
                }}
            />
        </SectionCard>
    );
}

/**
 * CA DER Sub-tab
 * Handles California DER charges configuration.
 */
function CADERSubTab({ scenarioForm }) {
    return (
        <SectionCard title="CA DER Charges" icon={<AttachMoney color="primary" />}>
            <CADERChargesPanel
                location={{
                    latitude: scenarioForm.tariffLocation?.lat,
                    longitude: scenarioForm.tariffLocation?.lon
                }}
                scenario={{
                    solar_kw: parseFloat(scenarioForm.pvMaxKw) || 0,
                    battery_kw: parseFloat(scenarioForm.storageMaxKw) || 0,
                    battery_kwh: parseFloat(scenarioForm.storageMaxKwh) || 0,
                    generator: scenarioForm.useGenerator ? {
                        capacity_kw: (scenarioForm.generators?.length > 0)
                            ? scenarioForm.generators.reduce((sum, g) => sum + (parseFloat(g.kw) || 0), 0)
                            : (parseFloat(scenarioForm.generatorMaxKw) || 0),
                        fuel_type: scenarioForm.generatorFuelType || 'diesel'
                    } : null
                }}
                caDerConfig={scenarioForm.caDerConfig}
                onConfigChange={(config) => scenarioForm.setCaDerConfig?.(config)}
            />
        </SectionCard>
    );
}

/**
 * UtilitySection Component
 *
 * Main component for Tab 1 (Utility) content with sub-tabs.
 *
 * @param {Object} props
 * @param {Object} props.scenarioForm - Scenario form hook state
 * @param {Object} props.vortxSettings - Vortx-specific settings
 * @param {Function} props.setVortxSettings - Setter for vortx settings
 * @param {string} props.optimizationEngine - Current optimization engine
 * @param {string} props.projectId - Current project ID
 * @param {Object} props.vortxStyles - Styling object
 * @param {boolean} props.showValidation - Whether to show validation errors
 * @param {boolean} props.requiredTariff - Whether tariff requirement is met
 * @param {number} props.subTab - Current sub-tab index
 * @param {Function} props.setSubTab - Setter for sub-tab index
 */
function UtilitySection({
    scenarioForm,
    vortxSettings,
    setVortxSettings,
    optimizationEngine,
    projectId,
    vortxStyles,
    showValidation,
    requiredTariff,
    subTab = 0,
    setSubTab,
}) {
    // Use internal state if no external state provided
    const [internalSubTab, setInternalSubTab] = useState(0);
    const utilitySubTab = setSubTab ? subTab : internalSubTab;
    const setUtilitySubTab = setSubTab || setInternalSubTab;

    return (
        <>
            <Tabs
                value={utilitySubTab}
                onChange={(e, newValue) => setUtilitySubTab(newValue)}
                sx={{
                    mb: 3,
                    borderBottom: `1px solid ${alpha(vortxColors.gold, 0.3)}`,
                    '& .MuiTab-root': {
                        fontFamily: vortxTypography.body,
                        fontWeight: 600,
                        color: 'text.secondary',
                        '&.Mui-selected': { color: vortxColors.gold },
                    },
                    '& .MuiTabs-indicator': { backgroundColor: vortxColors.gold },
                }}
            >
                <Tab icon={<ElectricBolt sx={{ fontSize: 18 }} />} iconPosition="start" label="Electricity" />
                <Tab icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" label="Fuel" />
                <Tab icon={<AttachMoney sx={{ fontSize: 18 }} />} iconPosition="start" label="CA DER Charges" />
            </Tabs>

            {/* Electricity Sub-tab */}
            {utilitySubTab === 0 && (
                <ElectricitySubTab
                    scenarioForm={scenarioForm}
                    vortxSettings={vortxSettings}
                    setVortxSettings={setVortxSettings}
                    optimizationEngine={optimizationEngine}
                    projectId={projectId}
                    showValidation={showValidation}
                    requiredTariff={requiredTariff}
                />
            )}

            {/* Fuel Sub-tab */}
            {utilitySubTab === 1 && (
                <FuelSubTab
                    scenarioForm={scenarioForm}
                    vortxStyles={vortxStyles}
                    projectId={projectId}
                />
            )}

            {/* CA DER Sub-tab */}
            {utilitySubTab === 2 && (
                <CADERSubTab scenarioForm={scenarioForm} />
            )}
        </>
    );
}

export default UtilitySection;
